/**
 * Ordering keys for the organizer board.
 *
 * A task's place in its column is a base-62 string, and moving a card means
 * computing a string that sorts strictly between its two new neighbours. That
 * makes a drag exactly one UPDATE of one row, and it means two people (or two
 * tabs) dragging at once cannot corrupt each other's neighbours.
 *
 * The alternatives both fail in practice. An integer `position` with a reindex
 * rewrites every row below the insertion point on every drag, and two
 * interleaved reorders produce an order neither client asked for. A float
 * midpoint runs out of mantissa after ~50 drops into the same gap and then
 * silently collapses to equal values.
 *
 * The cost of string ranks is that keys grow by roughly one character per
 * repeated insertion into the same gap, so `needsRebalance` exists to tell the
 * store when to rewrite a column back to short evenly-spaced keys.
 *
 * One invariant runs through all of this: **a rank never ends in the lowest
 * character**. Nothing can sort below "0", so a rank ending in "0" would be a
 * card that can never be dragged to the top of its column again.
 */

/** Base-62, in ASCII order, so plain string comparison is rank comparison. */
const ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const BASE = ALPHABET.length;
const MIN_INDEX = 0;
const MIN_CHAR = ALPHABET[MIN_INDEX]!;

/**
 * Rank for the very first task in an empty column. The middle of the alphabet,
 * so the first inserts above and below it stay short.
 */
export const INITIAL_RANK = 'U';

/**
 * Length at which a column is rewritten. Reached only by repeatedly dropping
 * into the same gap; normal use keeps keys at a handful of characters.
 */
export const MAX_RANK_LENGTH = 48;

/**
 * Minimum number of free slots per item when rebalancing. Spacing the rewritten
 * keys this loosely is what lets the next few drags land back in short keys
 * instead of immediately growing again.
 */
const REBALANCE_SPREAD = 4;

function indexAt(rank: string, position: number): number {
  const index = ALPHABET.indexOf(rank[position]!);
  if (index < 0) throw new Error(`Invalid rank character in "${rank}"`);
  return index;
}

/**
 * A rank that sorts strictly between `before` and `after`.
 *
 * `null` means "no neighbour on that side" — pass `before: null` to prepend and
 * `after: null` to append. Throws if the two are equal or inverted, because that
 * means the caller's view of the column is wrong, and inventing a value would
 * hide the bug behind a subtly mis-ordered board.
 */
export function rankBetween(before: string | null, after: string | null): string {
  if (before !== null && after !== null && before >= after) {
    throw new Error(`Cannot rank between "${before}" and "${after}": not in ascending order`);
  }

  let prefix = '';
  let position = 0;
  /** Whether `after` still constrains us, or the prefix already sorts below it. */
  let afterBinds = after !== null;

  for (;;) {
    const lower = before !== null && position < before.length ? indexAt(before, position) : -1;

    if (afterBinds && position >= after!.length) {
      // Unreachable for ranks this module generates: it would mean the prefix
      // has become equal to `after`, so anything we append sorts above it. Only
      // a rank ending in the lowest character can get here, which the invariant
      // rules out. Fail loudly rather than return a mis-ordered key.
      throw new Error(`Cannot rank below "${after}": it ends in the lowest character`);
    }
    const upper = afterBinds ? indexAt(after!, position) : BASE;

    if (lower + 1 < upper) {
      const mid = Math.floor((lower + upper) / 2);
      // Landing on the lowest character would produce a rank nothing can sort
      // below, so step into it as a prefix instead of ending on it. Doing so
      // already puts us strictly below `after`, which releases that bound.
      if (mid === MIN_INDEX) {
        prefix += MIN_CHAR;
        afterBinds = false;
        position += 1;
        continue;
      }
      return prefix + ALPHABET[mid]!;
    }

    // No room at this position: keep a character both bounds allow, then look
    // one position deeper.
    if (lower >= 0) {
      prefix += ALPHABET[lower]!;
      // Taking a character strictly below `after`'s means `after` can no longer
      // constrain the positions that follow.
      if (lower < upper) afterBinds = false;
      position += 1;
      continue;
    }

    // `before` has run out, so the lowest character is the only one available.
    prefix += MIN_CHAR;
    if (upper > MIN_INDEX) afterBinds = false;
    position += 1;
  }
}

/** Rank that sorts before every existing task in the column. */
export function rankFirst(currentFirst: string | null): string {
  return currentFirst === null ? INITIAL_RANK : rankBetween(null, currentFirst);
}

/** Rank that sorts after every existing task in the column. */
export function rankLast(currentLast: string | null): string {
  return currentLast === null ? INITIAL_RANK : rankBetween(currentLast, null);
}

/** True once a rank has grown long enough that the column should be rewritten. */
export function needsRebalance(rank: string): boolean {
  return rank.length > MAX_RANK_LENGTH;
}

function toFixedWidth(value: number, width: number): string {
  let out = '';
  let remaining = value;
  for (let i = 0; i < width; i += 1) {
    out = ALPHABET[remaining % BASE]! + out;
    remaining = Math.floor(remaining / BASE);
  }
  return out;
}

/**
 * Evenly spaced short ranks for `count` items, used when rewriting a column.
 *
 * Fixed-width keys wide enough to leave `REBALANCE_SPREAD` slots per item, so
 * the gaps are big enough that a bumped value can never collide with its
 * neighbour and subsequent drags stay short.
 */
export function evenlySpacedRanks(count: number): string[] {
  if (count <= 0) return [];

  let width = 1;
  while (BASE ** width < REBALANCE_SPREAD * (count + 1)) width += 1;

  const step = Math.floor(BASE ** width / (count + 1));

  return Array.from({ length: count }, (_, i) => {
    let value = (i + 1) * step;
    // Keep the invariant: never end on the lowest character. `step` is at least
    // REBALANCE_SPREAD, so nudging by one cannot reach the next item's value.
    if (value % BASE === MIN_INDEX) value += 1;
    return toFixedWidth(value, width);
  });
}
